import React, { useMemo, useState } from 'react';
import { NavLink, useLocation, useSearchParams } from 'react-router-dom';
import { useSession } from '../app/providers/SessionProvider.jsx';
import { signIn } from '../services/account/accountService.js';
import {
  approveTvDevice,
  formatTvActivationCode,
  normalizeTvActivationCode,
} from '../services/tvActivationService.js';
import '../styles/account.css';

function initialCode(params) {
  return formatTvActivationCode(
    params.get('code') || params.get('user_code') || params.get('userCode') || '',
  );
}

export function TvActivationPage() {
  const { ready, configured, session, user } = useSession();
  const [params] = useSearchParams();
  const location = useLocation();
  const [code, setCode] = useState(() => initialCode(params));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localSession, setLocalSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [approved, setApproved] = useState(false);

  const platform = useMemo(() => {
    const requested = String(params.get('platform') || '').trim().toLowerCase();
    if (requested) return requested;
    return location.pathname.toLowerCase().startsWith('/roku/') ? 'roku' : 'tv';
  }, [location.pathname, params]);

  const activeSession = localSession || session;
  const activeUser = activeSession?.user || user;

  async function loginForActivation() {
    if (!email.trim() || !password) throw new Error('Enter your WatchVIM email and password first.');
    const result = await signIn(email, password);
    setLocalSession(result.session);
    return result.session;
  }

  async function submitLogin(event) {
    event.preventDefault();
    setBusy(true); setError(''); setSuccess('');
    try {
      await loginForActivation();
      setSuccess('Signed in. Confirm the TV code, then activate this device.');
    } catch (err) {
      setError(err?.message || 'Unable to sign in.');
    } finally {
      setBusy(false);
    }
  }

  async function submitActivation(event) {
    event.preventDefault();
    setBusy(true); setError(''); setSuccess('');
    try {
      let authSession = activeSession;
      if (!authSession?.access_token) authSession = await loginForActivation();
      const result = await approveTvDevice({ code, accessToken: authSession?.access_token });
      const entitlementMessage = result?.entitlement?.hasAccess
        ? ' Your WatchVIM membership access is active.'
        : '';
      setApproved(true);
      setSuccess(`Your TV app is connected.${entitlementMessage} Return to your TV screen to continue.`);
    } catch (err) {
      setError(err?.message || 'Unable to activate this TV.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="account-page auth-page" data-tv-activation-platform={platform}>
      <div className="account-card auth-card">
        <span className="eyebrow">WatchVIM TV Activation</span>
        <h1>Connect your TV</h1>
        <p className="account-muted">
          Use the QR code or activation code shown by the WatchVIM TV app to connect this device to your existing WatchVIM account.
        </p>
        {platform === 'roku' ? (
          <p className="account-muted"><strong>Subscriptions are not purchased through Roku.</strong> Sign in with the WatchVIM account you already use on WatchVIM.com.</p>
        ) : null}

        {!configured ? <p className="account-message error" role="alert">WatchVIM account services are temporarily unavailable. Please try again shortly.</p> : null}
        {error ? <p className="account-message error" role="alert">{error}</p> : null}
        {success ? <p className="account-message success" role="status" aria-live="polite">{success}</p> : null}

        <form className="account-form" onSubmit={submitActivation}>
          <label className="account-field">
            <span>TV activation code</span>
            <input
              name="tv-activation-code"
              inputMode="text"
              autoComplete="one-time-code"
              placeholder="WV7-08163"
              value={code}
              disabled={approved}
              onChange={(event) => setCode(formatTvActivationCode(event.target.value))}
              onBlur={() => setCode(formatTvActivationCode(normalizeTvActivationCode(code)))}
              required
            />
          </label>

          {ready && activeUser ? (
            <p className="account-muted">Signed in as <strong>{activeUser.email || 'your WatchVIM account'}</strong>. Confirm the code above to connect this TV.</p>
          ) : (
            <>
              <label className="account-field">
                <span>Email</span>
                <input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
              </label>
              <label className="account-field">
                <span>Password</span>
                <input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required />
              </label>
              <button className="account-secondary" type="button" disabled={busy || !configured} onClick={submitLogin}>
                {busy ? 'Signing in…' : 'Sign In'}
              </button>
            </>
          )}

          <button className="account-primary" type="submit" disabled={busy || approved || !ready || !configured}>
            {approved ? 'TV App Activated' : busy ? 'Activating…' : 'Activate TV App'}
          </button>
        </form>

        <div className="account-links">
          <NavLink to="/forgot-password">Forgot password?</NavLink>
          <NavLink to="/membership">Need a WatchVIM account?</NavLink>
        </div>
        <p className="account-muted">TV codes expire quickly. If the code has expired, reopen Sign In on the TV app and scan the fresh QR code.</p>
      </div>
    </section>
  );
}
